import { motion } from 'motion/react';
import styles from './CircularProgress.module.scss';

interface CircularProgressProps {
  percent: number;
  size?: number;
}

export function CircularProgress({ percent, size = 200 }: CircularProgressProps) {
  const strokeWidth = 12;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  // 240 degrees of a circle (2/3 of full circle)
  const angleRange = 240;
  const startAngle = 150; // Start from bottom left
  const arcLength = (angleRange / 360) * circumference;

  // strokeDashoffset controls how much of the arc is filled (0 = fully filled, arcLength = empty)
  const strokeDashoffset = arcLength * (1 - percent / 100);

  // Color based on percentage - using CSS variables
  const getColor = () => {
    if (percent >= 90) return 'var(--success)';
    if (percent >= 70) return 'var(--nature-primary)';
    if (percent >= 50) return 'var(--warning)';
    return 'var(--error)';
  };

  // Determine text color class
  const getTextColorClass = () => {
    if (percent >= 90) return styles['circularProgress__text--excellent'];
    if (percent >= 70) return styles['circularProgress__text--good'];
    if (percent >= 50) return styles['circularProgress__text--fair'];
    return styles['circularProgress__text--needsWork'];
  };

  return (
    <div
      className={styles.circularProgress}
      style={{
        '--progress-size': `${size}px`,
      } as React.CSSProperties}
    >
      <svg
        className={styles.circularProgress__svg}
        width={size}
        height={size}
        style={{
          '--start-angle': `${startAngle}deg`,
        } as React.CSSProperties}
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="var(--border-default)"
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeDashoffset={0}
          strokeLinecap="round"
        />

        {/* Progress circle */}
        <motion.circle
          className={styles.circularProgress__circle}
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke={getColor()}
          strokeWidth={strokeWidth}
          strokeDasharray={`${arcLength} ${circumference}`}
          strokeLinecap="round"
          initial={{ strokeDashoffset: arcLength }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 1, ease: 'easeOut' }}
        />
      </svg>

      {/* Percentage text in center */}
      <div className={styles.circularProgress__textContainer}>
        <motion.div
          className={`${styles.circularProgress__text} ${getTextColorClass()}`}
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ duration: 0.5, delay: 0.5, type: 'spring', stiffness: 200 }}
        >
          {Math.round(percent)}%
        </motion.div>
        <div className={styles.circularProgress__label}>
          Score
        </div>
      </div>
    </div>
  );
}
